import { html, nothing, TemplateResult } from "lit/html.js";
import { when } from "lit/directives/when.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import '@components/whatField';
import '@components/common/checkbox';
import '@components/common/submitButton';
import '@components/typeahead/email';
import '@components/typeahead/location';
import LawComplianceComponent from "@components/lawcompliance";
import SocialButtonsTemplate from "./socialButtons";


const FormTemplate = (config: ProposalConfigs, model: IProposalModel): TemplateResult => {

  const {
    isEmptySearch,
    messages,
    captcha: {
      enabled: captchaEnabled,
      isTest: captchaTest
    },
    feature: {
      whatFieldInModalPopupEnabled = false,
      locationFieldInModalPopupEnabled = false,
    },
    subscription: {
      buttonAdditionalClass
    },
    page: {
      isJobPreviewPage
    },
    socialLogin
  } = config;
  const {
    loading,
    handleSubmit,
    handleWhatInput,
    handleWhereInput,
    handleEmailChange,
    handleSocialSubmit,
    handleCheckbox,
    subscriptionSource,
    formData: {
      what: {
        value: whatValue
      },
      where: {
        value: whereValue
      },
      marketingConsent: {
        value: tpmConsentValue
      } = {},
      email: {
        value: emailValue = '',
        isValid: emailIsValid = false,
        isValidDomain: isEmailDomainValid = false
      } = {}
    },
    description = '',
    formSubmitted,
  } = model;

  const keyDownHandler = (event: KeyboardEvent) => {
    const { key } = event;

    if (key.toLowerCase() === 'enter') {
      handleSubmit(new Event('submit'));
    }
  }

  const tpmConsentEnabled = config.feature.gdprThirdPartyMarketingSubscriptionEnabled;
  const tpmConsentBelow = config.feature.gdprThirdPartyMarketingSubscriptionBelowEnabled;
  const tpmConsentBorder = config.feature.gdprThirdPartyMarketingSubscriptionFrameEnabled;
  const whatFieldIsEnabled = subscriptionSource !== 'favourite' && (whatFieldInModalPopupEnabled || isEmptySearch || isJobPreviewPage);
  const whereFieldIsEnabled = subscriptionSource !== 'favourite' && (locationFieldInModalPopupEnabled || isEmptySearch || isJobPreviewPage);

  const locationTypeaheadPlaceholder = locationFieldInModalPopupEnabled && !isEmptySearch && !isJobPreviewPage
    ? messages.ProposalPopupLocationPlaceholder
    : messages.SearchWhere;

  const isLocationLabelEnabled = (locationFieldInModalPopupEnabled || isEmptySearch) && !isJobPreviewPage;

  const lawComplianceProps: ILawComplianceComponent = {
    messages,
    captchaEnabled,
    captchaTest,
  }


  return html`
    <div class="modal-body">
    ${when(subscriptionSource === 'favourite', () => html`
      <p class="title">${messages.JobSeekerSubscriptionFavouriteTitle}</p>
    `)}
      <div class="proposal-claim">
      ${when(subscriptionSource === 'favourite' && !isEmptySearch, () => html`
        <p class="description">${messages.JobSeekerSubscriptionFavouriteText}</p>
      `, () => html`
        <div class="description-wrapper">
          <p class="description">${unsafeHTML(description)}</p>
        </div>
      `)}
      </div>
      <form class="proposal-form" name="forms.proposal" novalidate @submit=${handleSubmit} @keydown=${keyDownHandler}>
      ${when(!whatValue && !whereValue, () => html`
        <div class="proposal-form--error-message">
            <span>${unsafeHTML(messages.JobSeekerSubscriptionDialogInvalidWLWarn)}</span>
        </div>
      `)}
      <div class="proposal-form__box"> 
      ${when(whatFieldIsEnabled, () => html`
        <div class="proposal-form__field">
          <what-field-component
            currentValue=${whatValue}
            additionalClasses=${!whatValue && !whereValue ? 'jr-input-material--invalid' : ''}
            labelText=${messages.RegistrationWhat}
            placeholder=${messages.SearchWhat}
            loading=${loading}
            @what-change-event=${handleWhatInput}
          />
        </div>
      `)}
      ${when(whereFieldIsEnabled, () => html`
        <div class="proposal-form__field">
          <location-typeahead-component
            currentValue=${whereValue} 
            isLocationLabelEnabled=${isLocationLabelEnabled} 
            labelText=${messages.ProposalPopupLocationLabel}
            placeholder=${locationTypeaheadPlaceholder}
            @location-change-event=${handleWhereInput}
            />
        </div>
      `)}
        </div>
        <div class="proposal-form__field">
        ${when(formSubmitted && !emailIsValid, () => html`
          <div class="proposal-form--error-message">
            <span>${messages.JobSeekerSubscriptionDialogInvalidEmailWarn}</span>
          </div>
        `)}
        ${when(formSubmitted && !isEmailDomainValid, () => html`
          <div class="proposal-form--error-message">
            <span>${messages.JobSeekerSubscriptionDialogInvalidEmailWarn}</span>
          </div>
        `)}
          <email-typeahead-component
            currentValue=${emailValue}
            labelText=${messages.JobSeekerSubscriptionDialogEmailLabel}
            placeholder=${messages.JobSeekerSubscriptionDialogEmailLabel}
            @email-change-event=${handleEmailChange}
          />
        </div>
        ${when(tpmConsentEnabled && !tpmConsentBelow, () => html`
        <div class="proposal-form__field">
            <checkbox-component additionalClasses=${tpmConsentBorder ? 'proposal-box-border' : ''} currentValue="${tpmConsentValue}" @checkbox-change-event=${handleCheckbox} checkboxDesc=${messages.JobSeekerSubscriptionTpmConsent}
            ></checkbox--component>
            </div>
            
        `)}  
        ${when(captchaEnabled && !captchaTest, () => html`<div id="modal-captcha-container"></div>`)}
        <submit-button-component
          btnText=${messages.JobSeekerSubscriptionDialogActivateButton}
          additionalClasses=${buttonAdditionalClass ? `jr-button__modal-submit--${buttonAdditionalClass}` : ''}
          disabled=${loading || undefined}
          @button-click-event=${handleSubmit}>
        </submit-button-component>
        ${when(tpmConsentEnabled && tpmConsentBelow, () => html`
        <div class="proposal-form__field">
            <checkbox-component  additionalClasses=${tpmConsentBorder ? 'proposal-box-border' : ''} currentValue="${tpmConsentValue}" @checkbox-change-event=${handleCheckbox} checkboxDesc=${messages.JobSeekerSubscriptionTpmConsent}
            ></checkbox--component>
            </div>
            
        `)}
        <hr class="consent-hr"></hr>
      </form>
      ${LawComplianceComponent(lawComplianceProps)}
      ${socialLogin.enabled && socialLogin.insidePopupEnabled && handleSocialSubmit ? SocialButtonsTemplate({ messages, captchaEnabled, socialLogin, handleSocialSubmit }) : nothing}
      </div>`;
}

export default FormTemplate;